import { NetworkInstance, WorldAssignmentFacade } from "@goodgamestudios/world-assignment";
import { getLogger } from "@goodgamestudios/logger";
import { EnvGlobalsHandler } from "../../EnvGlobalsHandler";
const logger = getLogger("CoreJS.InstanceVOProxy");
/**
 * class to manage all available instance VOs
 *
 * @author dkoehler
 *
 */
export class InstanceVOProxy {

    private worldAssignmentFacade: WorldAssignmentFacade;
    private _selectedInstanceVO: NetworkInstance;
    private _runtimeInstances: Map<number, NetworkInstance> = new Map<number, NetworkInstance>();

    public setWorldAssignementFacade(facade: WorldAssignmentFacade): void {
        this.worldAssignmentFacade = facade;
    }

    /** map with all instances. */
    public get instanceMap(): NetworkInstance[] {
        if (!this.worldAssignmentFacade) {
            logger.warn("worldassignment is null: are you invoking get InstanceMap too early?");
            return [];
        }

        var instanceList: NetworkInstance[] = this.worldAssignmentFacade.networkInstances.concat() as NetworkInstance[];
        if (EnvGlobalsHandler.globals.isTest) {
            instanceList = instanceList.concat(this._runtimeInstances.toArray()); // add testservers
        }
        return instanceList;
    }

    public get selectedInstanceVO(): NetworkInstance {
        if (!this._selectedInstanceVO) {
            return this.getSelectedWorldAssignmentInstance();
        }
        return this._selectedInstanceVO;
    }


    private getSelectedWorldAssignmentInstance(): NetworkInstance {
        if (!this.worldAssignmentFacade) {
            return null;
        }
        return this.worldAssignmentFacade.selectedNetworkInstance;
    }


    /**
     * add a new instance to this proxy.
     * ONLY USE THIS FOR TESTSERVERS
     *
     * @param NetworkInstance
     */
    // @deprecated("HTML-4220", "we should use a proxied network.xml instead of messing around at runtime ")
    public addInstance(NetworkInstance: NetworkInstance): void {
        if (!this.worldAssignmentFacade.getInstanceById(NetworkInstance.instanceId)) {
            this._runtimeInstances.set(NetworkInstance.instanceId, NetworkInstance);
        }
    }

    /**
     * Add new instances to this proxy
     * ONLY USE THIS FOR TESTSERVERS
     * @param instances Vector with instances to add
     */
    // @deprecated("HTML-4220", "we should use a proxied network.xml instead of messing around at runtime ")
    public addInstances(instances: NetworkInstance[]): void {
        logger.warn("deprecated(HTML-4220, we should use a proxied network.xml instead of messing around at runtime");
        for (const instance of instances) {
            this.addInstance(instance);
        }
    }


    public getInstanceVOByID(instanceID: number): NetworkInstance {
        if (this._runtimeInstances.has(instanceID))
            return this._runtimeInstances.get(instanceID);

        if (!this.worldAssignmentFacade) {
            logger.warn("worldassignment is null: are you invoking getInstanceVOByID too early?");
            return null;
        }
        return this.worldAssignmentFacade.getInstanceById(instanceID);
    }

    /**
     * get an NetworkInstance by ZoneID
     *
     * @return com.goodgamestudios.parser.network.vo.NetworkInstance
     */
    public getInstanceVOByZoneID(zoneId: number): NetworkInstance {
        if (!this.worldAssignmentFacade) {
            logger.warn("worldassignment is null: are you invoking getInstanceVOByZoneID too early?");
            return null;
        }
        return this.worldAssignmentFacade.getInstanceByZoneId(zoneId);
    }

    /**
     * Get all available instances for current country.
     *
     * The list is sorted:
     * First come the national instances, then the international ones (and finally the test instances):
     * 1. national instances (e.g. Germany 1)
     * 2. international instances
     * 3. test / dev instances
     *
     * Instance numbers (determined by &lt;instance&gt;&lt;instanceName&gt; in network.xml) are in descending order (e.g. „Germany 2“ over „Germany 1“).
     * Higher numbered instances are preferred - the reason being that new instances are created when
     * the existing reach their user number limits, so higher numbered instances are expected to have less users.
     *
     * @return list of sorted instances
     */

    public getInstancesForActualCountry(): NetworkInstance[] {
        //  array returned by facade is ReadOnly.   until we figure out (HTML-2006) if and where to mutate worldassignment stuff we expose it to the game ase mutable array
        if (!this.worldAssignmentFacade) {
            logger.warn("worldassignment is null: are you invoking getInstancesForActualCountry too early?");
            return [];
        }
        return this.worldAssignmentFacade.getInstancesByCountryCode(this.worldAssignmentFacade.currentCountry.ggsCountryCode) as NetworkInstance[];
    }


    public printInstances(): void {
        for (let NetworkInstance of this.instanceMap) {
            logger.debug(NetworkInstance.toString());
        }
    }


}
